import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppService } from '@nk/access-block';
import { SENSITIVE_DATA_API_SERVICE_TOKEN } from '@nk/api-lib';
import {
  AppIdentityCompositeEntity,
  SensitiveDataCompanyAccessEntity,
  SensitiveDataTypeEntity,
  SensitiveDataTypeKey,
  SensitiveDataWatchlistAccessEntity,
} from '@nk/data-entities-lib';
import { ButtonDirective, NkToastrService, SpinnerComponent } from '@nk/ui-lib';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { combineLatest, Observable, Subject } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { getToggleMessage, ToggleAction } from '../../utils/payment-remarks.utils';

// TODO: TESTS are needed, and probably it worth to divide this into two components, for enabling and disabling correspondingly
@Component({
  selector: 'nk-toggle-payment-remarks-modal',
  templateUrl: './toggle-payment-remarks-modal.component.html',
  imports: [TranslateModule, SpinnerComponent, ButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TogglePaymentRemarksModalComponent {
  // Set via BsModalService initialState — cannot use input() signals
  public action!: ToggleAction;
  public watchlistUuid!: string;
  public sensitiveDataWatchlistAccessUuid!: string;
  public companyUuid!: string;
  public sensitiveDataCompanyAccessUuid!: string;

  public onSuccess = new Subject<SensitiveDataWatchlistAccessEntity | SensitiveDataCompanyAccessEntity>();
  public isLoading = signal(false);

  private _bsModalRef = inject(BsModalRef);
  private _appService = inject(AppService);
  private _sensitiveDataService = inject(SENSITIVE_DATA_API_SERVICE_TOKEN);
  private _translateService = inject(TranslateService);
  private _toastr = inject(NkToastrService);

  public cancel(): void {
    this._bsModalRef.hide();
  }

  public confirm(action: ToggleAction): void {
    this._bsModalRef.hide();
    if (action === 'enable') {
      this._postEntity({ watchlistUuid: this.watchlistUuid, companyUuid: this.companyUuid });
    }
    if (action === 'disable') {
      this._deleteEntity({
        sensitiveDataWatchlistAccessUuid: this.sensitiveDataWatchlistAccessUuid,
        sensitiveDataCompanyAccessUuid: this.sensitiveDataCompanyAccessUuid,
      });
    }
  }

  private _postEntity({ watchlistUuid, companyUuid }: { watchlistUuid?: string; companyUuid?: string }): void {
    this.isLoading.set(true);
    combineLatest([
      this._appService.identityDataCache.get(),
      this._sensitiveDataService.sensitiveDataTypeListCache.get(),
    ])
      .pipe(
        switchMap(([identity, sensitiveDataTypes]) => {
          return this._switchPost(watchlistUuid, companyUuid, identity, sensitiveDataTypes);
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe(
        (data) => {
          this._invalidateCache();
          const message = getToggleMessage('success', watchlistUuid ? 'watchlist' : 'company');
          this._toastr.success(this._translateService.instant(message) as string);
          this.onSuccess.next(data);
        },
        () => {
          const message = getToggleMessage('error', watchlistUuid ? 'watchlist' : 'company');
          this._toastr.error(this._translateService.instant(message) as string);
        },
      );
  }

  private _switchPost(
    watchlistUuid: string | undefined,
    companyUuid: string | undefined,
    identity: AppIdentityCompositeEntity,
    sensitiveDataTypes: SensitiveDataTypeEntity[],
  ): Observable<SensitiveDataWatchlistAccessEntity> | Observable<SensitiveDataCompanyAccessEntity> {
    const sensitiveDataTypeUuid = sensitiveDataTypes.find(
      (i) => i.sensitive_data_type_key === SensitiveDataTypeKey.payment_remark,
    )?.uuid;
    if (!sensitiveDataTypeUuid) {
      throw new Error('No payment_remark sensitive data type found');
    }
    const basePayload = {
      app_customer_uuid: identity.app_customer.uuid,
      sensitive_data_type_uuid: sensitiveDataTypeUuid,
      app_membership_uuid: identity.app_membership.uuid,
    };
    if (watchlistUuid) {
      return this._sensitiveDataService.postSensitiveDataWatchlistAccessEntity({
        ...basePayload,
        watchlist_uuid: watchlistUuid,
      });
    }
    if (companyUuid) {
      return this._sensitiveDataService.postSensitiveDataCompanyAccessEntity({
        ...basePayload,
        company_uuid: companyUuid,
      });
    }
    throw new Error('No valid entity uuid provided');
  }

  private _deleteEntity({
    sensitiveDataWatchlistAccessUuid,
    sensitiveDataCompanyAccessUuid,
  }: {
    sensitiveDataWatchlistAccessUuid?: string;
    sensitiveDataCompanyAccessUuid?: string;
  }): void {
    this.isLoading.set(true);
    this._appService.identityDataCache
      .get()
      .pipe(
        switchMap((identity) =>
          this._switchDelete(sensitiveDataWatchlistAccessUuid, sensitiveDataCompanyAccessUuid, identity),
        ),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: (data) => {
          this._invalidateCache();
          const message = getToggleMessage('success', sensitiveDataWatchlistAccessUuid ? 'watchlist' : 'company');
          this._toastr.success(this._translateService.instant(message) as string);
          this.onSuccess.next(data);
        },
        error: () => {
          const message = getToggleMessage('error', sensitiveDataWatchlistAccessUuid ? 'watchlist' : 'company');
          this._toastr.error(this._translateService.instant(message) as string);
        },
      });
  }

  private _switchDelete(
    sensitiveDataWatchlistAccessUuid: string | undefined,
    sensitiveDataCompanyAccessUuid: string | undefined,
    identity: AppIdentityCompositeEntity,
  ): Observable<SensitiveDataWatchlistAccessEntity> | Observable<SensitiveDataCompanyAccessEntity> {
    if (sensitiveDataWatchlistAccessUuid) {
      return this._sensitiveDataService.deleteSensitiveDataWatchlistAccessEntity(
        sensitiveDataWatchlistAccessUuid,
        identity.app_customer.uuid,
      );
    }
    if (sensitiveDataCompanyAccessUuid) {
      return this._sensitiveDataService.deleteSensitiveDataCompanyAccessEntity(
        sensitiveDataCompanyAccessUuid,
        identity.app_customer.uuid,
      );
    }
    throw new Error('No valid entity uuid provided');
  }

  private _invalidateCache(): void {
    this._sensitiveDataService.companySensitiveDataCompositeListCache.invalidateAllCache();
    this._sensitiveDataService.sensitiveDataWatchlistAccessEntityListCache.invalidateAllCache();
    this._sensitiveDataService.sensitiveDataCompanyAccessEntityListCache.invalidateAllCache();
    this._sensitiveDataService.sensitiveDataCompositeByParamsCache.invalidateAllCache();
    this._sensitiveDataService.triggerSensitiveDataLoad();
  }
}
